import { useState, useEffect } from "react";

const initialState = {
  recordingMinutes: 0,
  recordingSeconds: 0,
  initRecording: false,
  mediaStream: null,
  mediaRecorder: null,
  audio: null,
  audioData: null,
};

export async function startRecording(setRecorderState) {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    setRecorderState((prevState) => ({
      ...prevState,
      initRecording: true,
      mediaStream: stream,
    }));
  } catch (err) {
    console.error(err);
  }
}

export function saveRecording(recorder) {
  if (recorder && recorder.state !== "inactive") recorder.stop();
}

export default function useRecorder() {
  const [recorderState, setRecorderState] = useState(initialState);

  const getSupportedMimeType = () => {
    if (MediaRecorder.isTypeSupported("audio/mp4")) {
      return "audio/mp4"; // iOS Safari supports this format
    } else if (MediaRecorder.isTypeSupported("audio/webm")) {
      return "audio/webm"; // Android Chrome supports this format
    } else {
      return "audio/wav"; // Fallback, large file size but supported widely
    }
  };

  useEffect(() => {
    let recordingInterval = null;
    if (recorderState.initRecording) {
      recordingInterval = setInterval(() => {
        setRecorderState((prevState) => {
          if (prevState.recordingSeconds < 59) {
            return {
              ...prevState,
              recordingSeconds: prevState.recordingSeconds + 1,
            };
          } else {
            return {
              ...prevState,
              recordingMinutes: prevState.recordingMinutes + 1,
              recordingSeconds: 0,
            };
          }
        });
      }, 1000);
    } else {
      clearInterval(recordingInterval);
    }
    return () => clearInterval(recordingInterval);
  }, [recorderState.initRecording]);

  useEffect(() => {
    if (recorderState.mediaStream) {
      const mimeType = getSupportedMimeType();
      setRecorderState((prevState) => ({
        ...prevState,
        mediaRecorder: new MediaRecorder(prevState.mediaStream, { mimeType }),
      }));
    }
  }, [recorderState.mediaStream]);

  useEffect(() => {
    const recorder = recorderState.mediaRecorder;
    let chunks = [];

    if (recorder) {
      recorder.start();
      recorder.ondataavailable = (event) => chunks.push(event.data);
      recorder.onstop = () => {
        const mimeType = getSupportedMimeType();
        const blob = new Blob(chunks, { type: mimeType });
        chunks = [];

        const fileExtension = mimeType === "audio/mp4" ? "m4a" : "webm";
        const audioFile = new File(
          [blob],
          `audio-${Date.now()}.${fileExtension}`,
          {
            type: mimeType,
          }
        );

        setRecorderState((prevState) => ({
          ...initialState,
          audio: URL.createObjectURL(blob),
          audioData: audioFile,
        }));
      };
    }

    return () => {
      if (recorder) {
        recorder.stream.getAudioTracks().forEach((track) => track.stop());
      }
    };
  }, [recorderState.mediaRecorder]);

  return {
    recorderState,
    startRecording: () => startRecording(setRecorderState),
    cancelRecording: () => setRecorderState(initialState),
    saveRecording: () => saveRecording(recorderState.mediaRecorder),
  };
}
